import React, { useContext, useEffect, useState } from 'react'
import {
    Autocomplete,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Stack,
    SelectChangeEvent,
    Card,
    CardContent,
    CardHeader,
    Typography,
} from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle'
import { KeDoContextType, QueryBuilderFieldProps } from './types'
import { KeDoContext } from '../../../kedo-context'
import { CompactButton } from '@/Components/Elements/StyledComponents/Buttons'
import { autocompleteGroupedSlotProps } from '@/Components/Elements/StyledComponents/styles'

type FieldOption = {
    value: string | number
    label: string
    group?: string
}

const QueryBuilderField: React.FC<QueryBuilderFieldProps> = ({
    field,
    index,
    dataField: initialDataField,
    onChange: onFieldChange,
    onDelete,
}) => {
    const kedo = useContext<KeDoContextType>(KeDoContext)

    const [id, setId] = useState<any>(field.id)
    const [uuid, setUuid] = useState<string>(field.uuid)
    const [type, setType] = useState<string | ''>(field.type ?? '')
    const [dataField, setDataField] = useState<any>(initialDataField)
    const [value, setValue] = useState<any>(field.value ?? '')
    const [error, setError] = useState<number | null>(null)

    const onChangeMultiple = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const values = Array.from(
            e.target.selectedOptions,
            (option) => option.value
        )

        const field: any = {
            id: id,
            value: values,
            type: type,
        }

        field[e.target.name] = values
        setValue(values)
        onFieldChange(index, field)
    }

    const onChange = (
        e: React.ChangeEvent<any> | SelectChangeEvent<string>
    ) => {
        const field: any = {
            id: id,
            value: value,
            type: type,
        }

        field[e.target.name] = e.target.value

        if (e.target.name === 'type') setType(e.target.value)
        if (e.target.name === 'value') setValue(e.target.value)
        onFieldChange(index, field)
    }

    const fetchField = () => {
        if (
            field.id === 'created_at' ||
            field.id === 'modified_at' ||
            field.id === 'created_by' ||
            field.id === 'modified_by'
        ) {
            setDataField(field)

            return
        }

        if (isNaN(field.id)) {
            return
        }

        const url = kedo.api().getDefDossierDefFieldEndpoint()
        kedo.api()
            .getCached(url + '/' + field.id)
            .then((response: any) => setDataField(response.data))
            .catch((err: any) => {
                setError(err.response.status)
            })
    }

    useEffect(() => {
        fetchField()
    }, [])

    const getFieldTitle = (dataField: any) => {
        if (dataField && dataField.name) {
            return dataField.name
        }

        if (!dataField || !dataField.id) {
            return ''
        }

        if (dataField.id === 'created_at') {
            return kedo.t('Created at')
        }
        if (dataField.id === 'created_by') {
            return kedo.t('Created by')
        }
        if (dataField.id === 'modified_at') {
            return kedo.t('Modified at')
        }
        if (dataField.id === 'modified_by') {
            return kedo.t('Modified by')
        }

        return dataField.id
    }

    const renderSettings = () => {
        if (!dataField) {
            return ''
        }

        let defField = dataField.def_field

        if (dataField.id === 'created_at' || dataField.id === 'modified_at') {
            defField = { type: 'systemdate' }
        } else if (
            dataField.id === 'created_by' ||
            dataField.id === 'modified_by'
        ) {
            defField = { type: 'systemuser' }
        }

        if (!defField || !defField.type) {
            return ''
        }

        const fieldType = defField.type

        switch (fieldType) {
            case 'list': {
                const listValueOptions: FieldOption[] = [
                    {
                        value: 'NULL',
                        label: kedo.t('Empty'),
                    },
                    ...(defField.def_field_lists?.map((opt: any) => ({
                        value: opt.id,
                        label: kedo.env().translateItem(opt, 'def_field_list'),
                    })) ?? []),
                ]

                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                displayEmpty
                                onChange={onChange}
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="equals">
                                    {kedo.t('is equal to')}
                                </MenuItem>
                                <MenuItem value="not_equals">
                                    {kedo.t('is NOT equal to')}
                                </MenuItem>
                                <MenuItem value="in">
                                    {kedo.t('is one of the selected values.')}
                                </MenuItem>
                                <MenuItem value="not_in">
                                    {kedo.t(
                                        'is NOT one of the selected values.'
                                    )}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Value')}
                            </Typography>
                            <Autocomplete
                                size="small"
                                options={listValueOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, selected) =>
                                    String(option.value) ===
                                    String(selected.value)
                                }
                                value={
                                    listValueOptions.find(
                                        (opt) =>
                                            String(opt.value) === String(value)
                                    ) ?? null
                                }
                                onChange={(_event, option) =>
                                    onChange({
                                        target: {
                                            name: 'value',
                                            value: option?.value ?? '',
                                        },
                                    } as any)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        name="value"
                                        placeholder={kedo.t('Choose an option')}
                                    />
                                )}
                            />
                        </FormControl>
                    </Stack>
                )
            }

            case 'user':
            case 'systemuser':
                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                onChange={onChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="equals">
                                    {kedo.t('is equal to')}
                                </MenuItem>
                                <MenuItem value="not_equals">
                                    {kedo.t('is NOT equal to')}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Value')}
                            </Typography>
                            <Select
                                name="value"
                                value={value}
                                size="small"
                                onChange={onChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                <MenuItem value="{user}">
                                    {kedo.t('the logged-in user himself')}
                                </MenuItem>
                                <MenuItem value="null">
                                    {kedo.t('Empty selection (0 users)')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                )

            case 'date':
            case 'systemdate': {
                const dateTypeOptions: FieldOption[] = [
                    {
                        value: 'equals',
                        label: kedo.t('Date equals to (exact match)'),
                        group: kedo.t('Conditions'),
                    },
                    {
                        value: 'not_equals',
                        label: kedo.t('Date is not equal to (exact match)'),
                        group: kedo.t('Conditions'),
                    },
                    {
                        value: 'date_gt',
                        label: 'Date is greater >',
                        group: kedo.t('Conditions'),
                    },
                    {
                        value: 'date_gte',
                        label: 'Date is greater or equals to >=',
                        group: kedo.t('Conditions'),
                    },
                    {
                        value: 'date_lt',
                        label: 'Date is lesser <',
                        group: kedo.t('Conditions'),
                    },
                    {
                        value: 'date_lte',
                        label: 'Date is lesser or equals to <=',
                        group: kedo.t('Conditions'),
                    },
                    {
                        value: 'max_days_old',
                        label: 'Add number of days to content. The result must be in the future (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'min_days_old',
                        label: 'Add number of days from content. The result must be in the past (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'max_weeks_old',
                        label: 'Add number of weeks to content. The result must be in the future (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'min_weeks_old',
                        label: 'Add number of weeks from content. The result must be in the past (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'max_months_old',
                        label: 'Add number of months to content. The result must be in the future (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'min_months_old',
                        label: 'Add number of months from content. The result must be in the past (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'max_days_to_go',
                        label: 'Add number of days to content. The result must be in the future.',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'min_days_to_go',
                        label: 'Add number of days from content. The result must be in the past.',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'max_years_old',
                        label: 'Add number of years to content. The result must be in the future (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'min_years_old',
                        label: 'Add number of years from content. The result must be in the past (or equals today).',
                        group: kedo.t('Add'),
                    },
                    {
                        value: 'max_days_old_subtract',
                        label: 'Subtract number of days to content. The result must be in the future (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'min_days_old_subtract',
                        label: 'Subtract number of days from content. The result must be in the past (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'max_weeks_old_subtract',
                        label: 'Subtract number of weeks to content. The result must be in the future (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'min_weeks_old_subtract',
                        label: 'Subtract number of weeks from content. The result must be in the past (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'max_months_old_subtract',
                        label: 'Subtract number of months to content. The result must be in the future (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'min_months_old_subtract',
                        label: 'Subtract number of months from content. The result must be in the past (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'max_days_to_go_subtract',
                        label: 'Subtract number of days to content. The result must be in the future.',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'min_days_to_go_subtract',
                        label: 'Subtract number of days from content. The result must be in the past.',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'max_years_old_subtract',
                        label: 'Subtract number of years to content. The result must be in the future (or equals today).',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'min_years_old_subtract',
                        label: 'Subtract number of years from content. The result must be in the past (or equals today)',
                        group: kedo.t('Subtract'),
                    },
                    {
                        value: 'date_between',
                        label: 'Date is between. Syntax for input field: DATE1,DATE2. The comma is mandatory.',
                        group: kedo.t('Between'),
                    },
                    {
                        value: 'between_days',
                        label: 'Between given number of days and TODAY',
                        group: kedo.t('Between'),
                    },
                    {
                        value: 'between_weeks',
                        label: 'Between given number of weeks and TODAY',
                        group: kedo.t('Between'),
                    },
                    {
                        value: 'between_months',
                        label: 'Between given number of months and TODAY',
                        group: kedo.t('Between'),
                    },
                    {
                        value: 'between_years',
                        label: 'Between given number of years and TODAY',
                        group: kedo.t('Between'),
                    },
                    {
                        value: 'birthday_weeks',
                        label: 'Given content date on today OR between TODAY and given number of weeks',
                        group: kedo.t('Between'),
                    },
                ]

                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Autocomplete
                                size="small"
                                slotProps={autocompleteGroupedSlotProps}
                                options={dateTypeOptions}
                                groupBy={(option) => option.group || ''}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, selected) =>
                                    String(option.value) ===
                                    String(selected.value)
                                }
                                value={
                                    dateTypeOptions.find(
                                        (opt) =>
                                            String(opt.value) === String(type)
                                    ) ?? null
                                }
                                onChange={(_event, option) =>
                                    onChange({
                                        target: {
                                            name: 'type',
                                            value: option?.value ?? '',
                                        },
                                    } as any)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        name="type"
                                        placeholder={kedo.t('Choose an option')}
                                    />
                                )}
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                Value (enter a number, NULL, NOT NULL, or a
                                date):
                            </Typography>
                            <TextField
                                size="small"
                                name="value"
                                value={value}
                                onChange={(e) =>
                                    onChange({
                                        target: {
                                            name: 'value',
                                            value: e.target.value,
                                        },
                                    } as any)
                                }
                            />
                        </FormControl>
                    </Stack>
                )
            }

            case 'textarea':
                return (
                    <Stack>
                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                name="type"
                                value={type}
                                size="small"
                                onChange={
                                    onChange as (
                                        e: SelectChangeEvent<string>
                                    ) => void
                                }
                                displayEmpty
                            >
                                <MenuItem value="">Choose an option</MenuItem>
                                <MenuItem value="string_empty">
                                    is EMPTY
                                </MenuItem>
                                <MenuItem value="string_is_not_empty">
                                    is NOT EMPTY
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                )

            case 'text':
            case 'autoIncrement':
            case 'calc':
            case 'url':
            case 'email':
            case 'amount': {
                const textTypeOptions: FieldOption[] = [
                    { value: 'string_empty', label: 'is EMPTY' },
                    { value: 'string_is_not_empty', label: 'is NOT EMPTY' },
                    {
                        value: 'string_exact_equals',
                        label: 'is equal to (exact match)',
                    },
                    {
                        value: 'string_exact_not_equals',
                        label: 'is NOT equal to (exact match)',
                    },
                    {
                        value: 'string_like_equals',
                        label: 'IS LIKE (pattern)',
                    },
                    {
                        value: 'string_not_like_equals',
                        label: 'IS NOT LIKE (pattern)',
                    },
                    {
                        value: 'string_numerical_equal',
                        label: 'Numeric input: equal =',
                    },
                    {
                        value: 'string_numerical_not_equal',
                        label: 'Numeric input: not equal <>',
                    },
                    { value: 'string_numerical_bt', label: 'Numeric: >' },
                    { value: 'string_numerical_bte', label: 'Numeric: >=' },
                    { value: 'string_numerical_st', label: 'Numeric: <' },
                    { value: 'string_numerical_ste', label: 'Numeric: <=' },
                ]

                return (
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Type')}
                            </Typography>
                            <Autocomplete
                                size="small"
                                options={textTypeOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, selected) =>
                                    String(option.value) ===
                                    String(selected.value)
                                }
                                value={
                                    textTypeOptions.find(
                                        (opt) =>
                                            String(opt.value) === String(type)
                                    ) ?? null
                                }
                                onChange={(_event, option) =>
                                    onChange({
                                        target: {
                                            name: 'type',
                                            value: option?.value ?? '',
                                        },
                                    } as any)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        name="type"
                                        placeholder={kedo.t('Choose an option')}
                                    />
                                )}
                            />
                        </FormControl>
                        <FormControl fullWidth>
                            <Typography
                                variant="body2"
                                component="label"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {kedo.t('Value')}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                name="value"
                                value={value}
                                onChange={(e) =>
                                    onChange({
                                        target: {
                                            name: 'value',
                                            value: e.target.value,
                                        },
                                    } as any)
                                }
                                InputProps={{
                                    readOnly:
                                        type === 'string_empty' ||
                                        type === 'string_is_not_empty',
                                }}
                            />
                        </FormControl>
                    </Stack>
                )
            }

            default:
                return null
        }
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={getFieldTitle(dataField)}
                sx={{
                    bgcolor: 'grey.900',
                    color: 'common.white',
                    py: 1.5,
                    px: 2.5,
                }}
                slotProps={{
                    action: {
                        sx: {
                            m: 0,
                            alignSelf: 'center',
                        },
                    },
                }}
                action={
                    <CompactButton dense onClick={() => onDelete(uuid)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </CompactButton>
                }
                subheader={
                    <Stack direction="row" spacing={1} mt={1}>
                        {error === 404 && (
                            <Chip
                                color="warning"
                                size="small"
                                icon={
                                    <FontAwesomeIcon
                                        icon={faExclamationCircle}
                                    />
                                }
                                label={kedo.t('Item not found')}
                            />
                        )}
                        {error && error !== 404 && (
                            <Chip
                                color="warning"
                                size="small"
                                icon={
                                    <FontAwesomeIcon
                                        icon={faExclamationCircle}
                                    />
                                }
                                label={kedo.t('Something went wrong')}
                            />
                        )}
                    </Stack>
                }
            />

            <CardContent sx={{ bgcolor: 'grey.100' }}>
                {renderSettings()}
            </CardContent>
        </Card>
    )
}

export default QueryBuilderField
